import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  enablePushNotifications,
  checkPushPermission,
} from "../lib/push";

// Settings card for iOS push notifications. Hidden on web (the push helper
// no-ops on non-native platforms, so the UI would be misleading).
// States:
//   - unsupported   → render nothing (web browser)
//   - prompt        → "Turn on notifications" button
//   - granted       → "Notifications are on" (dim state)
//   - denied        → explanatory copy + link to iOS Settings
export const PushSettingsCard = () => {
  const [perm, setPerm] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await checkPushPermission();
      if (!cancelled) setPerm(res?.receive || "unsupported");
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entirely on the web — push is an iOS/Android-only feature.
  if (perm === "loading" || perm === "unsupported") return null;

  const isGranted = perm === "granted";
  const isDenied = perm === "denied";

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await enablePushNotifications();
      if (res?.granted) {
        setPerm("granted");
        toast.success("Notifications enabled.");
      } else if (res?.reason === "denied") {
        setPerm("denied");
        toast.error("Notifications blocked. Enable in iOS Settings.");
      } else {
        toast.error("Could not enable notifications.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 mb-6 animate-fade-in-up"
      data-testid="push-settings-card"
    >
      <h2 className="font-outfit font-medium text-lg text-[#1B2349] mb-1 flex items-center gap-2">
        {isGranted ? <Bell className="w-5 h-5 text-[#3090A8]" /> : <BellOff className="w-5 h-5 text-[#718096]" />}
        Notifications
      </h2>
      <p className="text-sm text-[#718096] mb-5">
        {isGranted
          ? "You'll receive gentle reminders for your practices and weekly Calm Moments recaps."
          : isDenied
          ? "Notifications are blocked. To turn them on, open iOS Settings → Bilateral Calm → Notifications."
          : "Get gentle reminders for your practices and weekly Calm Moments recaps."}
      </p>
      {!isGranted && !isDenied && (
        <button
          onClick={handleEnable}
          disabled={busy}
          className="px-5 py-2.5 rounded-full bg-[#1B2349] text-white font-medium hover:bg-[#0F1838] transition-colors disabled:opacity-60 flex items-center gap-2"
          data-testid="push-enable-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
          Turn on notifications
        </button>
      )}
      {isGranted && (
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#E7F0F4] text-[#3090A8] text-sm font-medium"
          data-testid="push-status-granted"
        >
          <Bell className="w-3.5 h-3.5" />
          Notifications on
        </span>
      )}
    </div>
  );
};

export default PushSettingsCard;
